import { mock, instance, verify, when, anyNumber, anything, resetCalls, anyOfClass } from 'ts-mockito';
import { Router } from 'aurelia-router';
import { Customer } from '../../../models/customer';
import { State } from '../../../services/global-state';
import { PayrazrService } from '../../../services/payrazr';
import { ValidationHelpers } from '../../../services/validation-helpers';
import { I18N } from 'aurelia-i18n';
import { AddPaymentMethod } from './add-payment-method';
import { HpfPaymentMethodService } from '../../../services/hpf-payment-method.service';
import { Features } from '../../../models/features';
import { ServiceResponse } from '../../../models/service-response';
import { CheckPaymentMethod } from '../../../services/hpf-transaction-service';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('add-payment-method', () => {
			let component: AddPaymentMethod;
			const mockRouter: Router = mock(Router);
			const mockState: State = mock(State);
			const mockPayrazrService: PayrazrService = mock(PayrazrService);
			const mockI18N: I18N = mock(I18N);
			const mockHpfPaymentMethodService: HpfPaymentMethodService = mock(HpfPaymentMethodService);

			// Mock I18N Dependencies
			beforeEach(() => {
				const i18next = {
					t: (a, b) => a
				};
				when(mockI18N.i18next).thenReturn(i18next);
				spyOn(i18next, 't').and.returnValue('localized');
			});

			// Initialize Component
			beforeEach(() => {
				component = new AddPaymentMethod(
					instance(mockRouter),
					instance(mockState),
					instance(mockPayrazrService),
					instance(mockI18N),
					instance(mockHpfPaymentMethodService)
				);
			});

			it('should initialize component', () => {
				expect(component).toBeTruthy();
			});

			describe('activate()', () => {
				const cust = new Customer({});
				cust.firstName = 'Jon';
				cust.lastName = 'Doe';
				cust.primaryAddress = {
					zip: '12345'
				};
				when(mockState.customer).thenReturn(cust);

				const routeData = {
					paymentMethodType: 'Card',
					isCreateMethod: false,
					account: {}
				};

				it('should get card information from routeData', () => {
					const spyPopulateCard = spyOn(component, 'populateCard').and.stub();
					routeData.paymentMethodType = 'Card';
					routeData.account = {};
					component.activate(null, routeData);
					expect(component.paymentMethodType).toEqual(routeData.paymentMethodType);
					expect(component.isCreateMethod).toEqual(routeData.isCreateMethod);
					expect(spyPopulateCard).toHaveBeenCalled();
				});
				it('should get Ach information from routeData', () => {
					const spyPopulateAch = spyOn(component, 'populateAch').and.stub();
					routeData.paymentMethodType = 'Ach';
					routeData.account = {};
					component.activate(null, routeData);
					expect(component.paymentMethodType).toEqual(routeData.paymentMethodType);
					expect(component.isCreateMethod).toEqual(routeData.isCreateMethod);
					expect(spyPopulateAch).toHaveBeenCalled();
				});
				it('should get account holder info when no account is found', () => {
					routeData.account = null;
					component.activate(null, routeData);
					expect(component.accountHolderName).toEqual(cust.firstName + ' ' + cust.lastName);
					expect(component.zipCode).toEqual('12345');
				});
			});
			describe('populateCard()', () => {
				const cardAccount = {
					cardInfoKey: 'testCard',
					last4ofCard: 'testNumber',
					expirationDate: '01/20',
					name: '',
					zipCode: ''
				};

				it('should set card information', () => {
					const spyPopulateNameAndZipCode = spyOn(component, 'populateNameAndZipCode').and.stub();
					component.populateCard(cardAccount);
					expect(component.cardInfoKey).toEqual(cardAccount.cardInfoKey);
					expect(component.cardNumber).toEqual(cardAccount.last4ofCard);
					expect(component.expMonth).toEqual(cardAccount.expirationDate.substr(0, 2));
					expect(component.expYear).toEqual( '20' + cardAccount.expirationDate.substr(3, 2));
					expect(spyPopulateNameAndZipCode).toHaveBeenCalled();
				});

				it('should set default expiration date as of today on card information', () => {
					const spyPopulateNameAndZipCode = spyOn(component, 'populateNameAndZipCode').and.stub();

					var today = new Date();
					var currentMonth = (today.getMonth() + 1).toString().padStart(2, "0");
					var currentYear = today.getFullYear().toString().substr(2,2);
					cardAccount.expirationDate = `${currentMonth}/${currentYear}`;

					component.populateCard(cardAccount);
					expect(component.cardInfoKey).toEqual(cardAccount.cardInfoKey);
					expect(component.cardNumber).toEqual(cardAccount.last4ofCard);
					expect(component.expMonth).toEqual(cardAccount.expirationDate.substr(0, 2));
					expect(component.expYear).toEqual( '20' + cardAccount.expirationDate.substr(3, 2));
					expect(spyPopulateNameAndZipCode).toHaveBeenCalled();
				});

			});
			describe('populateAch()', () => {
				const checkAccount = {
					checkInfoKey: 'testCard',
					achAccountType: 'checking',
					aba: '123456789',
					last4ofAccount: 'testNumber',
				};

				it('should set Ach information', () => {
					const spyPopulateNameAndZipCode = spyOn(component, 'populateNameAndZipCode').and.stub();
					component.populateAch(checkAccount);
					expect(component.checkInfoKey).toEqual(checkAccount.checkInfoKey);
					expect(component.accountNumber).toEqual(checkAccount.last4ofAccount);
					expect(component.accountType).toEqual(checkAccount.achAccountType);
					expect(component.aba).toEqual(checkAccount.aba);
					expect(spyPopulateNameAndZipCode).toHaveBeenCalled();
				});
			});
			describe('populateNameAndZipCode()', () => {
				const params = {
					name: 'testName',
					zipCode: '12345'
				};

				it('should set name and zip', () => {
					component.populateNameAndZipCode(params);
					expect(component.accountHolderName).toEqual('testName');
					expect(component.zipCode).toEqual('12345');
				});
			});

			describe('validate()', () => {
				when(mockHpfPaymentMethodService.verifyCheckAccepted(anyNumber(), anyOfClass(CheckPaymentMethod), 'C'))
					.thenResolve(new ServiceResponse(
						{ isSuccessful: true, resultMessage: 'resolved', value: {} }));

				it('should succeed with valid Card', async () => {
					spyOn(ValidationHelpers, 'isValidCreditCardNumber').and.returnValue(true);
					spyOn(component, 'checkExpirationDate').and.returnValue(true);
					component.paymentMethodType = 'Card';
					component.isCreateMethod = true;
					component.cardNumber = '1234567890123';
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					const isValid = await component.validate();
					expect(component.formErrors.length).toEqual(0);
					expect(isValid).toBeTruthy();
					expect(component.checkExpirationDate).toHaveBeenCalled();
				});
				it('should succeed with valid Ach', async () => {
					spyOn(ValidationHelpers, 'isTransitNumber').and.returnValue(true);
					component.paymentMethodType = 'Ach';
					component.isCreateMethod = true;
					component.aba = '123456789';
					component.accountNumber = '123456789';
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					const isValid = await component.validate();
					expect(component.formErrors.length).toEqual(0);
					expect(isValid).toBeTruthy();
				});
				it('should fail without card number', async () => {
					spyOn(component, 'checkExpirationDate').and.returnValue(true);
					component.paymentMethodType = 'Card';
					component.isCreateMethod = true;
					component.cardNumber = null;
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					const isValid = await component.validate();
					expect(isValid).toBeFalsy();
					expect(component.formErrors.length).toEqual(1);
				});
				it('should fail with invalid card', async () => {
					spyOn(ValidationHelpers, 'isValidCreditCardNumber').and.returnValue(false);
					spyOn(component, 'checkExpirationDate').and.returnValue(false);
					component.paymentMethodType = 'Card';
					component.isCreateMethod = true;
					component.cardNumber = '123';
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					let isValid = await component.validate();
					expect(isValid).toBeFalsy();
					expect(component.formErrors.length).toEqual(2);

					component.cardNumber = '1234567890123';
					isValid = await component.validate();
					expect(isValid).toBeFalsy();
					expect(component.formErrors.length).toEqual(2);
				});
				it('should fail without card number', async () => {
					spyOn(component, 'checkExpirationDate').and.returnValue(true);
					component.paymentMethodType = 'Card';
					component.isCreateMethod = true;
					component.cardNumber = null;
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					const isValid = await component.validate();
					expect(isValid).toBeFalsy();
					expect(component.formErrors.length).toEqual(1);
				});
				it('should fail without Ach aba', async () => {
					spyOn(ValidationHelpers, 'isTransitNumber').and.returnValue(true);
					component.paymentMethodType = 'Ach';
					component.isCreateMethod = true;
					component.aba = null;
					component.accountNumber = '123456789';
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					const isValid = await component.validate();
					expect(component.formErrors.length).toEqual(1);
					expect(isValid).toBeFalsy();
				});
				it('should fail with invalid Ach aba', async () => {
					spyOn(ValidationHelpers, 'isTransitNumber').and.returnValue(false);
					component.paymentMethodType = 'Ach';
					component.isCreateMethod = true;
					component.aba = '123';
					component.accountNumber = '123456789';
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					let isValid = await component.validate();
					expect(component.formErrors.length).toEqual(1);
					expect(isValid).toBeFalsy();

					component.aba = '123456789';
					isValid = await component.validate();
					expect(component.formErrors.length).toEqual(1);
					expect(isValid).toBeFalsy();
				});
				it('should fail with invalid Ach account', async () => {
					spyOn(ValidationHelpers, 'isTransitNumber').and.returnValue(true);
					component.paymentMethodType = 'Ach';
					component.isCreateMethod = true;
					component.aba = '123456789';
					component.accountNumber = null;
					component.accountHolderName = 'Jon Doe';
					component.zipCode = '12345';
					let isValid = await component.validate();
					expect(component.formErrors.length).toEqual(1);
					expect(isValid).toBeFalsy();
				});
				it('should fail for missing name ar zip', async () => {
					spyOn(ValidationHelpers, 'isValidCreditCardNumber').and.returnValue(true);
					spyOn(component, 'checkExpirationDate').and.returnValue(true);
					component.paymentMethodType = 'Card';
					component.isCreateMethod = true;
					component.cardNumber = '1234567890123';
					component.accountHolderName = null;
					component.zipCode = null;
					let isValid = await component.validate();
					expect(component.formErrors.length).toEqual(2);
					expect(isValid).toBeFalsy();

					component.accountHolderName = 'Jon';
					component.zipCode = '123';
					isValid = await component.validate();
					expect(component.formErrors.length).toEqual(2);
					expect(isValid).toBeFalsy();
				});
			});
		});
	});
});
